import { JournalEntrySourceFilter as JournalEntrySourceFilterValue } from "@common/schemas/domains/accounting/journalEntry";
import { Form1099BoxFilterValue } from "@common/constants/domains/accounting/form1099Box";
import React from "react";
import classNames from "classnames";
import { addYears } from "date-fns";
import { FiltersTypes } from "@common/types/filters/general";
import { WORK_DATE_VISIBLE_PRESETS } from "@helpers/date";
import {
    FinancialAccount,
    FinancialAccountSourceType,
} from "@common/types/financialAccount";
import { AccountClass } from "@common/constants/domains/accounting/accounts";
import { CounterpartyDirection } from "@common/helpers/counterparties";
import { BillStatusFilter } from "@components/pageHeader/filters/Tabs/BillStatusFilter/BillStatusFilter";
import { CombinedBillStatusFilter } from "@components/pageHeader/filters/Tabs/CombinedBillStatusFilter/CombinedBillStatusFilter";
import { OverDueDateFilter } from "@components/pageHeader/filters/Tabs/OverDueDateFilter/OverDueDateFilter";
import { DueDateFilter } from "@components/pageHeader/filters/Tabs/DueDateFilter/DueDateFilter";
import { JournalEntrySourceFilter } from "@components/pageHeader/filters/Tabs/JournalEntrySourceFilter/JournalEntrySourceFilter";
import { TriangleIcon } from "@icons";
import { AggregatedDateFilter } from "@components/pageHeader/filters/AggregatedFilters/DateFilter/AggregatedDateFilter";
import { AggregatedFiltersOverlay } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFiltersOverlay";
import { CounterpartyFilter } from "@components/pageHeader/filters/AggregatedFilters/CounterpartyFilter/CounterpartyFilter";
import { CustomerFilter } from "@components/pageHeader/filters/AggregatedFilters/CustomerFilter/CustomerFilter";
import { CounterpartyCreatedByFilter } from "@components/pageHeader/filters/Tabs/CounterpartyCreatedByFilter/CounterpartyCreatedByFilter";
import { AggregatedAccountsFilter } from "@components/pageHeader/filters/AggregatedFilters/AccountFilter/AggregatedAccountsFilter";

import { FinancialDocumentStatusFilter } from "./Tabs/FinancialDocumentStatusFilter/FinancialDocumentStatusFilter";
import { DocumentTypeFilter } from "./Tabs/DocumentTypeFilter/DocumentTypeFilter";
import { AggregatedClassFilter } from "./Tabs/ClassFilter/AggregatedClassFilter";
import { DirectionRadioDef } from "./Tabs/AmountFilter/directionRadios";
import { TransactionAmountFilter } from "./Tabs/AmountFilter/TransactionAmountFilter";
import { TransactionRulesFilter } from "./Tabs/RulesFilter/TransactionRulesFilter";
import { SimilarToFilter } from "./Tabs/SimilarToFilter/SimilarToFilter";
import { NoteFilter } from "./Tabs/NoteFilter/NoteFilter";
import { ReceiptsFilter } from "./Tabs/ReceiptsFilter/ReceiptsFilter";
import { MatchedFilter } from "./Tabs/MatchedFilter/MatchedFilter";
import { MatchStatusFilter } from "./Tabs/MatchStatusFilter/MatchStatusFilter";
import { CashItemTypeFilter } from "./Tabs/CashItemTypeFilter/CashItemTypeFilter";
import { TriStateBooleanFilter } from "./Tabs/TriStateBooleanFilter/TriStateBooleanFilter";
import {
    DraftCounterpartyFilter,
    DraftOptionsFilter,
    isAccrualReviewStatus,
} from "./Tabs/AccrualDraftFilters/AccrualDraftFilters";
import { FilterMultiselectOption } from "./FilterMultiselect/FilterMultiselect";
import { SplitFilter } from "./Tabs/SplitFilter/SplitFilter";
import { ReconciliationFilter } from "./Tabs/ReconciliationFilter/ReconciliationFilter";
import { AggregatedCategoryFilter } from "./Tabs/CategoryFilter/AggregatedCategoryFilter";
import { DocumentCategoryFilter } from "./Tabs/DocumentCategoryFilter/DocumentCategoryFilter";
import {
    FiltersChangeFn,
    filterValueMapping,
    FilterTabs,
    VIRTUAL_FILTER_TABS,
} from "./lib";
import styles from "./FiltersOverlay.module.scss";
import { AccountCodesFilter } from "./Tabs/AccountCodesFilter/AccountCodesFilter";
import { AccountStatusFilter } from "./Tabs/AccountStatusFilter/AccountStatusFilter";
import { InvoiceStatusFilter } from "./Tabs/InvoiceStatusFilter/InvoiceStatusFilter";
import { CombinedInvoiceStatusFilter } from "./Tabs/CombinedInvoiceStatusFilter/CombinedInvoiceStatusFilter";
import { BillableExpenseInvoiceStatusFilter } from "./Tabs/BillableExpenseInvoiceStatusFilter/BillableExpenseInvoiceStatusFilter";
import { CatalogItemFilter } from "./Tabs/CatalogItemFilter/CatalogItemFilter";
import { InvoiceSourceTemplateFilter } from "./Tabs/InvoiceSourceTemplateFilter/InvoiceSourceTemplateFilter";
import {
    CatalogItemIncomeAccountFilter,
    CatalogItemReceivablesAccountFilter,
} from "./Tabs/CatalogItemLedgerAccountFilter/CatalogItemLedgerAccountFilter";
import { SubscriptionFilter } from "./Tabs/SubscriptionFilter/SubscriptionFilter";
import { DisabledCounterpartyFilter } from "./Tabs/DisabledCounterpartyFilter/DisabledCounterpartyFilter";
import { Eligible1099Filter } from "./Tabs/Eligible1099Filter/Eligible1099Filter";
import { Exclude1099Filter } from "./Tabs/Exclude1099Filter/Exclude1099Filter";
import { RuleStatusFilter } from "./Tabs/RuleStatusFilter/RuleStatusFilter";
import { RuleSourceFilter } from "./Tabs/RuleSourceFilter/RuleSourceFilter";
import { RuleActionFilter } from "./Tabs/RuleActionFilter/RuleActionFilter";
import { CoaAccountFilter } from "./Tabs/CoaAccountFilter/CoaAccountFilter";
import { GeneralLedgerFilter } from "./Tabs/GeneralLedgerFilter/GeneralLedgerFilter";
import { SplitAccountFilter } from "./Tabs/SplitAccountFilter/SplitAccountFilter";
import { AccrualCoaAccountFilter } from "./Tabs/AccrualCoaAccountFilter/AccrualCoaAccountFilter";
import { TeamMembersFilter } from "./Tabs/TeamMembersFilter/TeamMembersFilter";
import { TaskCreatedByFilter } from "./Tabs/TaskCreatedByFilter/TaskCreatedByFilter";
import { TaskCompletedByFilter } from "./Tabs/TaskCompletedByFilter/TaskCompletedByFilter";
import { PlanFilter } from "./Tabs/PlanFilter/PlanFilter";
import { WhoPaysFilter } from "./Tabs/WhoPaysFilter/WhoPaysFilter";
import { ServicesFilter } from "./Tabs/ServicesFilter/ServicesFilter";
import { LegalTypeFilter } from "./Tabs/LegalTypeFilter/LegalTypeFilter";
import { EntityIndustryFilter } from "./Tabs/EntityIndustryFilter/EntityIndustryFilter";
import { EntityBasisFilter } from "./Tabs/EntityBasisFilter/EntityBasisFilter";
import { CloseStageFilter } from "./Tabs/CloseStageFilter/CloseStageFilter";
import { PriorityFilter } from "./Tabs/PriorityFilter/PriorityFilter";
import { TransactionLockedFilter } from "./Tabs/TransactionLockedFilter/TransactionLockedFilter";
import { AccountSourceFilter } from "./Tabs/AccountSourceFilter/AccountSourceFilter";
import { AccountRolePresenceFilter } from "./Tabs/AccountRolePresenceFilter/AccountRolePresenceFilter";
import { AccountRulePresenceFilter } from "./Tabs/AccountRulePresenceFilter/AccountRulePresenceFilter";
import { Form1099BoxFilter } from "./Tabs/Form1099BoxFilter/Form1099BoxFilter";
import { CoaTemplateGroupsFilter } from "./Tabs/CoaTemplateGroupsFilter/CoaTemplateGroupsFilter";
import { CoaTemplateGroupOption } from "./Tabs/CoaTemplateGroupsFilter/CoaTemplateGroupsFilter.lib";

import { TransactionStatusFilter } from "./Tabs/TransactionStatusFilter/TransactionStatusFilter";
import { UnreadFilter } from "./Tabs/UnreadFilter/UnreadFilter";
import { WorkDeliveryDateFilter } from "./Tabs/WorkDeliveryDateFilter/WorkDeliveryDateFilter";
import { TemplateCounterpartyFilter } from "./Tabs/TemplateCounterpartyFilter/TemplateCounterpartyFilter";
import { TemplateCustomerFilter } from "./Tabs/TemplateCustomerFilter/TemplateCustomerFilter";
import { TemplateClassFilter } from "./Tabs/TemplateClassFilter/TemplateClassFilter";
import { TemplateBasisFilter } from "./Tabs/TemplateBasisFilter/TemplateBasisFilter";
import { TemplateOwnerFilter } from "./Tabs/TemplateOwnerFilter/TemplateOwnerFilter";
import { TemplateLastEditedByFilter } from "./Tabs/TemplateLastEditedByFilter/TemplateLastEditedByFilter";
import { TemplateCreatedByFilter } from "./Tabs/TemplateCreatedByFilter/TemplateCreatedByFilter";
import { TemplateTypeFilter } from "./Tabs/TemplateTypeFilter/TemplateTypeFilter";
import { TaskCloseFilter } from "./Tabs/TaskCloseFilter/TaskCloseFilter";
import { TaskAssigneeFilter } from "./Tabs/TaskAssigneeFilter/TaskAssigneeFilter";
import { TaskPriorityFilter } from "./Tabs/TaskPriorityFilter/TaskPriorityFilter";
import { TaskAttachmentsFilter } from "./Tabs/TaskAttachmentsFilter/TaskAttachmentsFilter";
import { TaskGroupFilter } from "./Tabs/TaskGroupFilter/TaskGroupFilter";
import { TaskDependenciesFilter } from "./Tabs/TaskDependenciesFilter/TaskDependenciesFilter";
import { TaskChatFilter } from "./Tabs/TaskChatFilter/TaskChatFilter";
import { TaskStageFilter } from "./Tabs/TaskStageFilter/TaskStageFilter";
import { OrgCloseStageFilter } from "./Tabs/OrgCloseStageFilter/OrgCloseStageFilter";
import { CloseStatusFilter } from "./Tabs/CloseStatusFilter/CloseStatusFilter";
import { OrgTaskTemplateTypeFilter } from "./Tabs/OrgTaskTemplateFilters/OrgTaskTemplateTypeFilter";
import { OrgTaskTemplateCreatedByFilter } from "./Tabs/OrgTaskTemplateFilters/OrgTaskTemplateCreatedByFilter";
import { OrgTaskTemplateGroupFilter } from "./Tabs/OrgTaskTemplateFilters/OrgTaskTemplateGroupFilter";
import { OrgTaskTemplateAssigneeRoleFilter } from "./Tabs/OrgTaskTemplateFilters/OrgTaskTemplateAssigneeRoleFilter";
import { OrgServiceFilter } from "./Tabs/OrgServiceFilter/OrgServiceFilter";
import { CloseTemplateFilter } from "./Tabs/CloseTemplateFilter/CloseTemplateFilter";
import { ApplicationTypeFilter } from "./Tabs/ApplicationTypeFilter/ApplicationTypeFilter";
import { ProgressFilter } from "./Tabs/ProgressFilter/ProgressFilter";
import { HealthFilter } from "./Tabs/HealthFilter/HealthFilter";
import { DaysInStageFilter } from "./Tabs/DaysInStageFilter/DaysInStageFilter";
import { ActorFilter } from "./Tabs/ActorFilter/ActorFilter";
import { ReviewFilter } from "./Tabs/ReviewFilter/ReviewFilter";
import { RuleReviewFilter } from "./Tabs/RuleReviewFilter/RuleReviewFilter";
import { CounterpartyReviewFilter } from "./Tabs/CounterpartyReviewFilter/CounterpartyReviewFilter";
import { EngagementTeamFilter } from "./Tabs/EngagementTeamFilter/EngagementTeamFilter";
import { SchedulePeriodFilter } from "./Tabs/SchedulePeriodFilter/SchedulePeriodFilter";
import { ScheduleTemplateFilter } from "./Tabs/ScheduleTemplateFilter/ScheduleTemplateFilter";
import { InvoiceTermsFilter } from "./Tabs/InvoiceTermsFilter/InvoiceTermsFilter";
import { InvoiceSentStatusFilter } from "./Tabs/InvoiceSentStatusFilter/InvoiceSentStatusFilter";
import { AgingPeriodFilter } from "./Tabs/AgingPeriodFilter/AgingPeriodFilter";
import { HoldingAccountFilter } from "./Tabs/SchedulesReportFilters/HoldingAccountFilter";
import { SchedulesReportFilterOption } from "./Tabs/SchedulesReportFilters/OptionsMultiselectFilter";
import { SchedulesReportGroupFilter } from "./Tabs/SchedulesReportFilters/SchedulesReportGroupFilter";
import { SchedulesReportTemplateFilter } from "./Tabs/SchedulesReportFilters/SchedulesReportTemplateFilter";
import { ScheduleClientFilter } from "./Tabs/ScheduleClientFilter/ScheduleClientFilter";
import { WorkClientFilter } from "./Tabs/WorkClientFilter/WorkClientFilter";
import { TaskClientFilter } from "./Tabs/TaskClientFilter/TaskClientFilter";
import { LedgerGroupFilter } from "./Tabs/LedgerGroupFilter/LedgerGroupFilter";
import { FilterNavTab } from "./FilterNavTab";
import { FilterTabPanel } from "./FilterTabPanel";
import { FilterTabIconMapping } from "./FilterTabIconMapping";
import { getFilterTabLabel } from "./FilterTabLabelMapping";

// Work start/end and service period filters need to allow picking dates well
// into the future (upcoming scheduled work), unlike most other date filters
// which default to "today" as their max. 10 years mirrors the horizon used
// for schedule preview generation (see scheduleWizardOccurrences.util.ts).
// Computed per call (not once at module load) so a long-lived SPA session
// doesn't keep shrinking the effective horizon as the days pass.
const getFarFutureMaxDate = () => addYears(new Date(), 10);

interface Props {
    directionSwitchDisabled?: boolean;
    directionSwitchReadonly?: boolean;
    directionRadios?: DirectionRadioDef[];
    showAmountFilterLevel?: boolean;
    filters: FiltersTypes;
    onChange: FiltersChangeFn;
    availableFilters: FilterTabs[];
    hardcodedEntityId?: number;
    availableClassifications?: AccountClass[];
    staticAccountTypeNodes?: Array<
        import("./Tabs/AccountTypeFilter/AccountTypeFilter").AccountTypeNode
    >;
    staticTemplateGroupOptions?: CoaTemplateGroupOption[];
    staticHoldingAccountOptions?: SchedulesReportFilterOption[];
    accountFilterSourceTypes?: FinancialAccountSourceType[];
    accountFilterPredicate?: (account: FinancialAccount) => boolean;
    onlyStandardCategories?: boolean;
    shouldShowNoAccountOption?: boolean;
    shouldShowKickAccountOption?: boolean;
    hideCoaAccountLastChangedBy?: boolean;
    valuePickersOnly?: boolean;
    /**
     * Word used in place of "counterparty" in the counterparty filter tab
     * and its active filter chip (e.g. "customer" on Invoices, "vendor" on
     * Bills). Defaults to the generic "Counterparty".
     */
    counterpartyEntityLabel?: string;
    counterpartyDirection?: CounterpartyDirection;
    counterpartySingleSelect?: boolean;
    /**
     * Restricts the counterparty filter's listed options to counterparties
     * used as the customer on at least one invoice. Used by the Invoices
     * page and AR Aging report filter overlays.
     */
    counterpartyInvoicesOnly?: boolean;
    hasTaskCreatedByClientOption?: boolean;
    hasTaskAssigneeOrgMembersOnly?: boolean;
    hasTaskAssignedToClientOption?: boolean;
    /**
     * Label for the ledger filter tab. Defaults to "General ledger".
     * Transactions overrides this to "Cash ledger".
     */
    ledgerFilterLabel?: string;
    tabLabels?: Partial<Record<FilterTabs, string>>;
    draftStatusOptions?: FilterMultiselectOption[];
    draftSourceOptions?: FilterMultiselectOption[];
    journalEntrySourceOptions?: JournalEntrySourceFilterValue[];
    form1099BoxOptions?: Form1099BoxFilterValue[];
}

export const FiltersOverlay = React.memo<Props>(
    ({
        filters,
        onChange,
        directionSwitchDisabled = false,
        directionSwitchReadonly = false,
        directionRadios,
        showAmountFilterLevel,
        availableFilters,
        hardcodedEntityId,
        availableClassifications,
        staticTemplateGroupOptions,
        staticHoldingAccountOptions,
        accountFilterSourceTypes,
        accountFilterPredicate,
        onlyStandardCategories = false,
        shouldShowNoAccountOption,
        shouldShowKickAccountOption,
        hideCoaAccountLastChangedBy = false,
        valuePickersOnly = false,
        counterpartyEntityLabel,
        counterpartyDirection,
        counterpartySingleSelect,
        counterpartyInvoicesOnly,
        hasTaskCreatedByClientOption,
        hasTaskAssigneeOrgMembersOnly,
        hasTaskAssignedToClientOption,
        ledgerFilterLabel,
        tabLabels,
        draftStatusOptions = [],
        draftSourceOptions = [],
        journalEntrySourceOptions,
        form1099BoxOptions,
    }) => (
        <AggregatedFiltersOverlay
            filterClassname={styles.filter}
            navigation={
                <>
                    {availableFilters
                        .filter(
                            (filterTab) =>
                                !VIRTUAL_FILTER_TABS.includes(filterTab),
                        )
                        .map((filterTab) => (
                            <FilterNavTab
                                eventKey={filterTab}
                                key={filterTab}
                                className={styles.navigationButton}
                            >
                                <span className={styles.navigationItem}>
                                    <span className={styles.navigationIcon}>
                                        {FilterTabIconMapping[filterTab]}
                                    </span>
                                    {getFilterTabLabel(filterTab, {
                                        counterpartyEntityLabel,
                                        ledgerFilterLabel,
                                        tabLabels,
                                    })}
                                    <TriangleIcon
                                        className={classNames(
                                            styles.navigationActiveIndicator,
                                            "icon-color-gray-500",
                                        )}
                                    />
                                </span>
                            </FilterNavTab>
                        ))}
                </>
            }
            content={
                <>
                    <FilterTabPanel eventKey={FilterTabs.DATE}>
                        <AggregatedDateFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>
                    <FilterTabPanel eventKey={FilterTabs.CATEGORY}>
                        <AggregatedCategoryFilter
                            filters={filters}
                            onChange={onChange}
                            onlyStandardCategories={onlyStandardCategories}
                        />
                    </FilterTabPanel>
                    <FilterTabPanel
                        className={styles.noPadding}
                        eventKey={FilterTabs.CLASS}
                    >
                        <AggregatedClassFilter
                            filters={filters}
                            onChange={onChange}
                            showFilterTypeSettings={!valuePickersOnly}
                        />
                    </FilterTabPanel>
                    <FilterTabPanel
                        eventKey={FilterTabs.AMOUNT}
                        className={styles.noPadding}
                    >
                        <TransactionAmountFilter
                            filters={filters}
                            onChange={onChange}
                            directionSwitchDisabled={directionSwitchDisabled}
                            directionSwitchReadonly={directionSwitchReadonly}
                            directionRadios={directionRadios}
                            showFilterLevel={showAmountFilterLevel}
                        />
                    </FilterTabPanel>
                    <FilterTabPanel eventKey={FilterTabs.RULES}>
                        <TransactionRulesFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>
                    <FilterTabPanel
                        eventKey={FilterTabs.SIMILAR_TO}
                        className={styles.noPadding}
                    >
                        <SimilarToFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>
                    <FilterTabPanel eventKey={FilterTabs.COUNTERPARTY}>
                        <CounterpartyFilter
                            filters={filters}
                            onChange={onChange}
                            hidePresenceRows={
                                valuePickersOnly || counterpartySingleSelect
                            }
                            direction={counterpartyDirection}
                            singleSelect={counterpartySingleSelect}
                            entityLabel={counterpartyEntityLabel}
                            invoiceCounterpartiesOnly={counterpartyInvoicesOnly}
                        />
                    </FilterTabPanel>
                    <FilterTabPanel eventKey={FilterTabs.CUSTOMER}>
                        <CustomerFilter filters={filters} onChange={onChange} />
                    </FilterTabPanel>
                    <FilterTabPanel
                        eventKey={FilterTabs.COUNTERPARTY_CREATED_BY}
                    >
                        <CounterpartyCreatedByFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>
                    <FilterTabPanel eventKey={FilterTabs.NOTE}>
                        <NoteFilter filters={filters} onChange={onChange} />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.RECEIPTS}>
                        <ReceiptsFilter filters={filters} onChange={onChange} />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.MATCHED}>
                        <MatchedFilter filters={filters} onChange={onChange} />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.MATCH_STATUS}>
                        <MatchStatusFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.DRAFT_SOURCE}>
                        <DraftOptionsFilter
                            name="draftSources"
                            options={draftSourceOptions}
                            value={filters.draftSources}
                            onChange={(selected) => {
                                const draftSources = selected.map(String);
                                onChange({
                                    draftSources:
                                        draftSources.length > 0
                                            ? draftSources
                                            : undefined,
                                });
                            }}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.DRAFT_COUNTERPARTY}>
                        <DraftCounterpartyFilter
                            value={filters.draftCounterpartyStates}
                            onChange={(draftCounterpartyStates) =>
                                onChange({ draftCounterpartyStates })
                            }
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.DRAFT_DUE_DATE}>
                        <TriStateBooleanFilter
                            name="hasDueDate"
                            value={filters.hasDueDate}
                            onChange={(hasDueDate) => onChange({ hasDueDate })}
                            trueOption={{ value: "filled", label: "Filled" }}
                            falseOption={{ value: "missing", label: "Missing" }}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.DRAFT_STATUS}>
                        <DraftOptionsFilter
                            name="accrualReviewStatuses"
                            options={draftStatusOptions}
                            value={filters.accrualReviewStatuses}
                            onChange={(selected) => {
                                const accrualReviewStatuses = selected.filter(
                                    isAccrualReviewStatus,
                                );
                                onChange({
                                    accrualReviewStatuses:
                                        accrualReviewStatuses.length > 0
                                            ? accrualReviewStatuses
                                            : undefined,
                                });
                            }}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.SPLIT}>
                        <SplitFilter filters={filters} onChange={onChange} />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.RECONCILIATION}>
                        <ReconciliationFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.CASH_ITEM_TYPE}>
                        <CashItemTypeFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.ACCOUNT}>
                        <AggregatedAccountsFilter
                            filters={filters}
                            onChange={onChange}
                            sourceTypes={accountFilterSourceTypes}
                            accountFilterPredicate={accountFilterPredicate}
                            shouldShowNoAccountOption={
                                shouldShowNoAccountOption
                            }
                            shouldShowKickAccountOption={
                                shouldShowKickAccountOption
                            }
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.DOCUMENT_STATUS}>
                        <FinancialDocumentStatusFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.DOCUMENT_CATEGORY}>
                        <DocumentCategoryFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.ACCOUNT_CODE}
                        className={styles.noPadding}
                    >
                        <AccountCodesFilter
                            filters={filters}
                            onChange={onChange}
                            hardcodedEntityId={hardcodedEntityId}
                            availableClassifications={availableClassifications}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.ACCOUNT_STATUS}>
                        <AccountStatusFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.ACCOUNT_SOURCE}>
                        <AccountSourceFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.ACCOUNT_ROLE_PRESENCE}>
                        <AccountRolePresenceFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.ACCOUNT_RULE_PRESENCE}>
                        <AccountRulePresenceFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.FORM_1099_BOX}>
                        <Form1099BoxFilter
                            filters={filters}
                            onChange={onChange}
                            boxOptions={form1099BoxOptions}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.COA_TEMPLATE_GROUPS}>
                        <CoaTemplateGroupsFilter
                            filters={filters}
                            onChange={onChange}
                            options={staticTemplateGroupOptions ?? []}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.JOURNAL_ENTRY_SOURCES}>
                        <JournalEntrySourceFilter
                            filters={filters}
                            onChange={onChange}
                            sourceOptions={journalEntrySourceOptions}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.INVOICE_STATUSES}
                        className={styles.noPadding}
                    >
                        <InvoiceStatusFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.INVOICE_STATUS_FILTER}
                        className={styles.noPadding}
                    >
                        <CombinedInvoiceStatusFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.BILLABLE_EXPENSE_INVOICE_STATUSES}
                        className={styles.noPadding}
                    >
                        <BillableExpenseInvoiceStatusFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.CATALOG_ITEM}
                        className={styles.noPadding}
                    >
                        <CatalogItemFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.INVOICE_SOURCE_TEMPLATE}
                    >
                        <InvoiceSourceTemplateFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.SUBSCRIPTION}>
                        <SubscriptionFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.ELIGIBLE_1099}
                        className={styles.noPadding}
                    >
                        <Eligible1099Filter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.EXCLUDE_1099}
                        className={styles.noPadding}
                    >
                        <Exclude1099Filter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.BILL_STATUSES}
                        className={styles.noPadding}
                    >
                        <BillStatusFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.BILL_STATUS_FILTER}
                        className={styles.noPadding}
                    >
                        <CombinedBillStatusFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.OVERDUE}>
                        <OverDueDateFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.DUE}>
                        <DueDateFilter filters={filters} onChange={onChange} />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.RULES_STATUS}
                        className={styles.noPadding}
                    >
                        <RuleStatusFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.RULES_SOURCE}
                        className={styles.noPadding}
                    >
                        <RuleSourceFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.RULES_ACTION}
                        className={styles.noPadding}
                    >
                        <RuleActionFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.LEDGER}
                        className={styles.noPadding}
                    >
                        <CoaAccountFilter
                            filters={filters}
                            onChange={onChange}
                            hideLastChangedBy={
                                hideCoaAccountLastChangedBy || valuePickersOnly
                            }
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.SPLIT_ACCOUNT}
                        className={styles.noPadding}
                    >
                        <SplitAccountFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.CASH_GENERAL_LEDGER}>
                        <GeneralLedgerFilter
                            target="cash"
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.ACCRUAL_GENERAL_LEDGER}
                    >
                        <GeneralLedgerFilter
                            target="accrual"
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.ACCRUAL_LEDGER}
                        className={styles.noPadding}
                    >
                        <AccrualCoaAccountFilter
                            filters={filters}
                            onChange={onChange}
                            hideNoAccountOption={valuePickersOnly}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.CATALOG_ITEM_INCOME_ACCOUNT}
                        className={styles.noPadding}
                    >
                        <CatalogItemIncomeAccountFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.CATALOG_ITEM_RECEIVABLES_ACCOUNT}
                        className={styles.noPadding}
                    >
                        <CatalogItemReceivablesAccountFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.PLANS}>
                        <PlanFilter filters={filters} onChange={onChange} />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.WHO_PAYS}>
                        <WhoPaysFilter filters={filters} onChange={onChange} />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.SERVICES}>
                        <ServicesFilter filters={filters} onChange={onChange} />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.ASSIGNEE}>
                        <TeamMembersFilter
                            filters={{
                                ...filters,
                                teamMemberIds: filters.assigneeIds,
                            }}
                            onChange={(payload) =>
                                onChange({ assigneeIds: payload.teamMemberIds })
                            }
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.LEGAL_TYPE}>
                        <LegalTypeFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.ENTITY_INDUSTRY}>
                        <EntityIndustryFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.ENTITY_BASIS}>
                        <EntityBasisFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.TRANSACTION_LOCKED}
                        className={styles.noPadding}
                    >
                        <TransactionLockedFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.STATUS}>
                        <TransactionStatusFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.TEAM_MEMBERS}>
                        <TeamMembersFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.START_DATE}>
                        <AggregatedDateFilter
                            filters={filterValueMapping[
                                FilterTabs.START_DATE
                            ].to(filters)}
                            onChange={(newValue) => {
                                onChange(
                                    filterValueMapping[
                                        FilterTabs.START_DATE
                                    ].from(newValue),
                                );
                            }}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.CLOSE_STAGE}>
                        <CloseStageFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.ORG_CLOSE_STAGE}>
                        <OrgCloseStageFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.CLOSE_STATUS}>
                        <CloseStatusFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.ORG_SERVICE}>
                        <OrgServiceFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.CLOSE_TEMPLATE}>
                        <CloseTemplateFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.APPLICATION_TYPE}>
                        <ApplicationTypeFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.PROGRESS}>
                        <ProgressFilter filters={filters} onChange={onChange} />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.HEALTH}>
                        <HealthFilter filters={filters} onChange={onChange} />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.WORK_CLIENT}>
                        <WorkClientFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.PRIORITY}>
                        <PriorityFilter filters={filters} onChange={onChange} />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.LEDGER_GROUP}
                        className={styles.noPadding}
                    >
                        <LedgerGroupFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.CLOSE_DATE}>
                        <AggregatedDateFilter
                            filters={filterValueMapping[
                                FilterTabs.CLOSE_DATE
                            ].to(filters)}
                            onChange={(newValue) => {
                                onChange(
                                    filterValueMapping[
                                        FilterTabs.CLOSE_DATE
                                    ].from(newValue),
                                );
                            }}
                            maxDate={getFarFutureMaxDate()}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.WORK_START_DATE}>
                        <AggregatedDateFilter
                            filters={filterValueMapping[
                                FilterTabs.WORK_START_DATE
                            ].to(filters)}
                            onChange={(newValue) => {
                                onChange(
                                    filterValueMapping[
                                        FilterTabs.WORK_START_DATE
                                    ].from(newValue),
                                );
                            }}
                            maxDate={getFarFutureMaxDate()}
                            visiblePresets={WORK_DATE_VISIBLE_PRESETS}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.WORK_DELIVERY_DATE}>
                        <WorkDeliveryDateFilter
                            filters={filters}
                            onChange={onChange}
                            maxDate={getFarFutureMaxDate()}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.APPLICATION_DATE}>
                        <AggregatedDateFilter
                            filters={filterValueMapping[
                                FilterTabs.APPLICATION_DATE
                            ].to(filters)}
                            onChange={(newValue) => {
                                onChange(
                                    filterValueMapping[
                                        FilterTabs.APPLICATION_DATE
                                    ].from(newValue),
                                );
                            }}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.COA_LAST_UPDATED}>
                        <AggregatedDateFilter
                            filters={filterValueMapping[
                                FilterTabs.COA_LAST_UPDATED
                            ].to(filters)}
                            onChange={(newValue) => {
                                onChange(
                                    filterValueMapping[
                                        FilterTabs.COA_LAST_UPDATED
                                    ].from(newValue),
                                );
                            }}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.COA_APPLIED_AT}>
                        <AggregatedDateFilter
                            filters={filterValueMapping[
                                FilterTabs.COA_APPLIED_AT
                            ].to(filters)}
                            onChange={(newValue) => {
                                onChange(
                                    filterValueMapping[
                                        FilterTabs.COA_APPLIED_AT
                                    ].from(newValue),
                                );
                            }}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.DAYS_IN_STAGE}>
                        <DaysInStageFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.DUE_DATE}>
                        <AggregatedDateFilter
                            filters={filterValueMapping[FilterTabs.DUE_DATE].to(
                                filters,
                            )}
                            onChange={(newValue) => {
                                onChange(
                                    filterValueMapping[
                                        FilterTabs.DUE_DATE
                                    ].from(newValue),
                                );
                            }}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.UNREAD}>
                        <UnreadFilter filters={filters} onChange={onChange} />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.UPLOADER}>
                        <ActorFilter
                            filters={{
                                ...filters,
                                actorIds: filters.uploaderIds,
                                actorType: filters.uploaderType,
                                sourceTypes: filters.uploaderSources,
                            }}
                            onChange={(payload) =>
                                onChange({
                                    ...("actorIds" in payload
                                        ? { uploaderIds: payload.actorIds }
                                        : {}),
                                    ...("actorType" in payload
                                        ? { uploaderType: payload.actorType }
                                        : {}),
                                    ...("sourceTypes" in payload
                                        ? {
                                              uploaderSources:
                                                  payload.sourceTypes,
                                          }
                                        : {}),
                                })
                            }
                            integrationsAsDocumentSources
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.DOCUMENT_PERIOD}>
                        <AggregatedDateFilter
                            filters={filterValueMapping[
                                FilterTabs.DOCUMENT_PERIOD
                            ].to(filters)}
                            onChange={(newValue) => {
                                onChange(
                                    filterValueMapping[
                                        FilterTabs.DOCUMENT_PERIOD
                                    ].from(newValue),
                                );
                            }}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.UPLOAD_DATE}>
                        <AggregatedDateFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.DOCUMENT_TYPE}>
                        <DocumentTypeFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.TEMPLATE_COUNTERPARTY}>
                        <TemplateCounterpartyFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.TEMPLATE_CUSTOMER}>
                        <TemplateCustomerFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.TEMPLATE_CLASS}>
                        <TemplateClassFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.TEMPLATE_BASIS}>
                        <TemplateBasisFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.TEMPLATE_LAST_EDITED_BY}
                    >
                        <TemplateLastEditedByFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.TEMPLATE_OWNER}>
                        <TemplateOwnerFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.TEMPLATE_CREATED_BY}>
                        <TemplateCreatedByFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.TEMPLATE_TYPE}>
                        <TemplateTypeFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.TASK_ASSIGNEE}>
                        <TaskAssigneeFilter
                            filters={filters}
                            onChange={onChange}
                            hasOrgMembersOnly={hasTaskAssigneeOrgMembersOnly}
                            hasClientOption={hasTaskAssignedToClientOption}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.TASK_CREATED_BY}>
                        <TaskCreatedByFilter
                            filters={filters}
                            onChange={onChange}
                            hasClientOption={hasTaskCreatedByClientOption}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.TASK_CLIENT}>
                        <TaskClientFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.TASK_CREATED_ON}>
                        <AggregatedDateFilter
                            filters={filterValueMapping[
                                FilterTabs.TASK_CREATED_ON
                            ].to(filters)}
                            onChange={(newValue) => {
                                onChange(
                                    filterValueMapping[
                                        FilterTabs.TASK_CREATED_ON
                                    ].from(newValue),
                                );
                            }}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.TASK_COMPLETED_BY}>
                        <TaskCompletedByFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.TASK_COMPLETED_ON}>
                        <AggregatedDateFilter
                            filters={filterValueMapping[
                                FilterTabs.TASK_COMPLETED_ON
                            ].to(filters)}
                            onChange={(newValue) => {
                                onChange(
                                    filterValueMapping[
                                        FilterTabs.TASK_COMPLETED_ON
                                    ].from(newValue),
                                );
                            }}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.TASK_CLOSE}>
                        <TaskCloseFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.TASK_PRIORITY}>
                        <TaskPriorityFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.TASK_ATTACHMENTS}>
                        <TaskAttachmentsFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.TASK_GROUP}>
                        <TaskGroupFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.TASK_DEPENDENCIES}
                        className={styles.noPadding}
                    >
                        <TaskDependenciesFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.TASK_CHAT}
                        className={styles.noPadding}
                    >
                        <TaskChatFilter filters={filters} onChange={onChange} />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.TASK_STAGE}>
                        <TaskStageFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.ORG_TASK_TEMPLATE_TYPE}
                    >
                        <OrgTaskTemplateTypeFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.ORG_TASK_TEMPLATE_GROUP}
                    >
                        <OrgTaskTemplateGroupFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.ORG_TASK_TEMPLATE_ASSIGNEE_ROLE}
                    >
                        <OrgTaskTemplateAssigneeRoleFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.ORG_TASK_TEMPLATE_CREATED_BY}
                    >
                        <OrgTaskTemplateCreatedByFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.REVIEW}
                        className={styles.noPadding}
                    >
                        <ReviewFilter filters={filters} onChange={onChange} />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.RULES_REVIEW}
                        className={styles.noPadding}
                    >
                        <RuleReviewFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.COUNTERPARTY_REVIEW}
                        className={styles.noPadding}
                    >
                        <CounterpartyReviewFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.COUNTERPARTY_DISABLED}
                        className={styles.noPadding}
                    >
                        <DisabledCounterpartyFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.ENGAGEMENT_TEAM}
                        className={styles.noPadding}
                    >
                        <EngagementTeamFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.SCHEDULE_PERIOD}>
                        <SchedulePeriodFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.SCHEDULE_TEMPLATE}>
                        <ScheduleTemplateFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.SCHEDULE_CLIENT}>
                        <ScheduleClientFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.SCHEDULES_REPORT_TEMPLATE}
                    >
                        <SchedulesReportTemplateFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel
                        eventKey={FilterTabs.SCHEDULES_REPORT_GROUP}
                    >
                        <SchedulesReportGroupFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.HOLDING_ACCOUNT}>
                        <HoldingAccountFilter
                            filters={filters}
                            onChange={onChange}
                            options={staticHoldingAccountOptions ?? []}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.TERM_START}>
                        <AggregatedDateFilter
                            filters={filterValueMapping[
                                FilterTabs.TERM_START
                            ].to(filters)}
                            onChange={(newValue) => {
                                onChange(
                                    filterValueMapping[
                                        FilterTabs.TERM_START
                                    ].from(newValue),
                                );
                            }}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.TERM_END}>
                        <AggregatedDateFilter
                            filters={filterValueMapping[FilterTabs.TERM_END].to(
                                filters,
                            )}
                            onChange={(newValue) => {
                                onChange(
                                    filterValueMapping[
                                        FilterTabs.TERM_END
                                    ].from(newValue),
                                );
                            }}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.INVOICE_TERMS}>
                        <InvoiceTermsFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.INVOICE_SENT_STATUS}>
                        <InvoiceSentStatusFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.INVOICE_CREATED_ON}>
                        <AggregatedDateFilter
                            filters={filterValueMapping[
                                FilterTabs.INVOICE_CREATED_ON
                            ].to(filters)}
                            onChange={(newValue) => {
                                onChange(
                                    filterValueMapping[
                                        FilterTabs.INVOICE_CREATED_ON
                                    ].from(newValue),
                                );
                            }}
                            maxDate={getFarFutureMaxDate()}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.INVOICE_DATE}>
                        <AggregatedDateFilter
                            filters={filterValueMapping[
                                FilterTabs.INVOICE_DATE
                            ].to(filters)}
                            onChange={(newValue) => {
                                onChange(
                                    filterValueMapping[
                                        FilterTabs.INVOICE_DATE
                                    ].from(newValue),
                                );
                            }}
                            maxDate={getFarFutureMaxDate()}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.INVOICE_DUE_DATE}>
                        <AggregatedDateFilter
                            filters={filterValueMapping[
                                FilterTabs.INVOICE_DUE_DATE
                            ].to(filters)}
                            onChange={(newValue) => {
                                onChange(
                                    filterValueMapping[
                                        FilterTabs.INVOICE_DUE_DATE
                                    ].from(newValue),
                                );
                            }}
                            maxDate={getFarFutureMaxDate()}
                        />
                    </FilterTabPanel>

                    <FilterTabPanel eventKey={FilterTabs.AGING_PERIOD}>
                        <AgingPeriodFilter
                            filters={filters}
                            onChange={onChange}
                        />
                    </FilterTabPanel>
                </>
            }
        />
    ),
);
FiltersOverlay.displayName = "FiltersOverlay";
